import { Link } from "react-router-dom";
import { AppointmentActivity } from "appointment-experience-client";
import {
  AppointmentActivityAction,
  DEFAULT_STATUS_LABELS,
  activityTypeConfig,
} from "../../../constants/AppointmentActivity";

export interface ActivityRowItem {
  key: string;
  /** Short uppercase type label: the icon's tooltip and screen-reader name. */
  tag: string;
  /** Material Symbols icon marking the item's type. */
  icon: string;
  /** The activity type, for the icon's colour. */
  tagClass: string;
  text: string;
  /** The item's status in words ("Started", "Completed"), shown as a badge. */
  status: string | null;
  link: string | null;
  /** Muted text at the end of the row, such as the time. */
  meta?: string;
}

/** A recorded activity as a row, worded the same wherever it's listed. */
export const activityRowItem = (activity: AppointmentActivity): ActivityRowItem => {
  const config = activityTypeConfig(activity.activityType);
  const status = activity.status as AppointmentActivityAction;
  return {
    key: activity.id,
    tag: config.tag,
    icon: config.icon,
    tagClass: activity.activityType,
    text: activity.title || config.label,
    status:
      config.statusLabels?.[status] ?? DEFAULT_STATUS_LABELS[status] ?? activity.status,
    link:
      config.buildLink?.({
        patientId: activity.patientId,
        refId: activity.refId,
        metadata: activity.metadata,
      }) ?? null,
  };
};

/**
 * One activity: a tinted type icon, its name and a status badge. Shared by the
 * appointment history and the visit's own activity so they read the same. A
 * linked row is clickable across its whole width.
 */
export const ActivityRow = ({ item }: { item: ActivityRowItem }) => (
  <li className={`activity-row${item.link ? " activity-row--link" : ""}`}>
    <span
      className={`activity-row__icon activity-row__icon--${item.tagClass} material-symbols-outlined`}
      role="img"
      aria-label={item.tag.toLowerCase()}
      title={item.tag.charAt(0) + item.tag.slice(1).toLowerCase()}
    >
      {item.icon}
    </span>
    {item.link ? (
      <Link className="activity-row__text" to={item.link} title={item.text}>
        {item.text}
      </Link>
    ) : (
      <span className="activity-row__text" title={item.text}>
        {item.text}
      </span>
    )}
    {item.status && <span className="activity-row__status">{item.status}</span>}
    {item.meta && <span className="activity-row__meta">{item.meta}</span>}
  </li>
);
